import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Scissors, Image as ImageIcon, Layers, 
  Wand2, Zap, LogOut, FileText, History as HistoryIcon,
  Users, Settings, Star
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDesign } from '../../context/DesignContext';

export const Sidebar: React.FC = () => {
  const { logout } = useAuth();
  const { setInputImage, setApiResult, setMaskData, setGeneralInstruction, setRedInstruction, setGreenInstruction } = useDesign();
  const navigate = useNavigate();

  const handleToolChange = () => {
    // Clear state when changing tools
    setInputImage(null);
    setApiResult(null);
    setMaskData(null);
    setGeneralInstruction('');
    setRedInstruction('');
    setGreenInstruction('');
  };

  const menuItems = [
    { id: 'listing', icon: FileText, label: 'Listing Optimizer', path: '/dashboard/listing' },
    { id: 'redesign', icon: Wand2, label: 'Redesign Tool', path: '/dashboard/redesign' },
    { id: 'mockup', icon: Layers, label: 'Mockup Generator', path: '/dashboard/mockup' },
    { id: 'enhance', icon: Zap, label: 'Product Enhancer', path: '/dashboard/enhance' },
    { id: 'clone', icon: Scissors, label: 'Design Extractor', path: '/dashboard/clone' },
    { id: 'embroidery', icon: Star, label: 'Embroidery Simulator', path: '/dashboard/embroidery' },
    { id: 'removebg', icon: ImageIcon, label: 'Background Remover', path: '/dashboard/removebg' },
    { id: 'upscale', icon: LayoutDashboard, label: 'Image Upscaler', path: '/dashboard/upscale' }, // Using LayoutDashboard as placeholder
  ];

  return (
    <aside className="w-64 border-r border-slate-200 bg-white flex flex-col hidden md:flex h-full">
      <div 
          onClick={() => navigate('/')} 
          className="p-6 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary-500/20">
            <Wand2 size={24} />
          </div>
          <div>
            <h1 className="font-bold text-slate-900 leading-tight">Listing<span className="text-primary-600">Studio</span></h1>
            <p className="text-xs text-slate-500 font-medium">AI Design Suite</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar">
        <div className="px-4 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Design Tools</div>
        {menuItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            onClick={handleToolChange}
            className={({ isActive }) => `
              w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
              ${isActive ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}
            `}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
          </NavLink>
        ))}

        <div className="mt-6 px-4 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Account</div>
        <NavLink to="/dashboard/history" onClick={handleToolChange} className={({ isActive }) => `w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>
            <HistoryIcon size={20} /><span>History</span>
        </NavLink>
        <NavLink to="/dashboard/profile" onClick={handleToolChange} className={({ isActive }) => `w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>
            <Users size={20} /><span>Profile</span>
        </NavLink>
        <NavLink to="/dashboard/admin" onClick={handleToolChange} className={({ isActive }) => `w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>
            <Settings size={20} /><span>Admin</span>
        </NavLink>
      </nav>

      <div className="p-4 border-t border-slate-100 bg-slate-50/50">
        <button onClick={() => logout()} className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors group">
          <LogOut size={20} className="group-hover:translate-x-1 transition-transform" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};
